import React, { useMemo } from "react";
import { Group, Button, Menu, ActionIcon } from "@mantine/core";
import { IconChevronDown } from "@tabler/icons-react";
import { PermissionRequirement } from "constants/types/permission";
import { useAbility } from "configs/casl";
import { canAccessRequirement } from "utils/helpers/authorization";

export interface ActionButton {
  key: string;
  label: string;
  icon?: React.ReactNode;
  variant?: "filled" | "light" | "outline" | "subtle" | "default";
  color?: string;
  isLoading?: boolean;
  disabled?: boolean;
  hidden?: boolean;
  isDanger?: boolean;
  permission?: PermissionRequirement;
  onClick: () => void;
}

interface SmartButtonGroupProps {
  actions: ActionButton[];
  maxVisible?: number;
  position?: "left" | "center" | "right" | "apart";
}

const GroupButton: React.FC<SmartButtonGroupProps> = ({
  actions,
  maxVisible = 3,
  position = "left",
}) => {
  const ability = useAbility();

  const availableActions = useMemo(
    () =>
      actions.filter((action) => {
        if (action.hidden) return false;
        // undefined permission → permissive (show), defined → check ability
        return canAccessRequirement(ability, action.permission, true);
      }),
    [actions, ability],
  );

  const { visibleActions, overflowActions } = useMemo(() => {
    const isSlightOverflow = availableActions.length === maxVisible + 1;
    const actualMax = isSlightOverflow ? maxVisible + 1 : maxVisible;
    return {
      visibleActions: availableActions.slice(0, actualMax),
      overflowActions: availableActions.slice(actualMax),
    };
  }, [availableActions, maxVisible]);

  if (availableActions.length === 0) return null;

  return (
    <Group justify={position} gap="sm" wrap="nowrap">
      {visibleActions.map((action) => (
        <Button
          key={action.key}
          leftSection={action.icon}
          variant={action.variant || "default"}
          color={action.isDanger ? "red" : action.color}
          loading={action.isLoading}
          disabled={action.disabled}
          onClick={action.onClick}
        >
          {action.label}
        </Button>
      ))}

      {overflowActions.length > 0 && (
        <Menu position="bottom-end" shadow="md" width={200}>
          <Menu.Target>
            <Button
              variant="default"
              rightSection={<IconChevronDown size={16} />}
            >
              Thao tác khác
            </Button>
          </Menu.Target>

          <Menu.Dropdown>
            {overflowActions.map((action) => (
              <Menu.Item
                key={action.key}
                leftSection={action.icon}
                color={action.isDanger ? "red" : action.color}
                disabled={action.disabled || action.isLoading}
                onClick={action.onClick}
              >
                {action.label}
              </Menu.Item>
            ))}
          </Menu.Dropdown>
        </Menu>
      )}
    </Group>
  );
};

export default GroupButton;
